import { createApi } from '@stilingue/st-auth-utils';
import Service from './Service';
import { isGMB, isInstagram } from '@/utils/channels';
import * as Sentry from '@sentry/browser';
import CryptoJS from 'crypto-js';
import { displaceDateRange } from '@/utils/url';

const gateway = process.env.VUE_APP_SERVICES_PROXY_URL || '';

const afterParser = (response) => {
  return response.data;
};

const removeGMBUsers = users => users.filter(({ channel }) => !isGMB(channel));
export const groupDuplicatedUsers = users => {
  const parsedUsers = [];
  users.forEach((user) => {
    const duplicatedInstagramUserIndex = parsedUsers.findIndex((igUser) => (
      isInstagram(igUser.channel) &&
      isInstagram(user.channel) &&
      igUser.username === user.username
    ));

    // Attachments is not used and can return an array with large number of indexes
    delete user.attachments;

    // HOTFIX - Back-end returns duplicated users for IG confusing UX
    if (duplicatedInstagramUserIndex >= 0) {
      const duplicatedUsers = parsedUsers[duplicatedInstagramUserIndex].duplicatedUsers || [];

      duplicatedUsers.push(user);

      parsedUsers[duplicatedInstagramUserIndex] = {
        ...parsedUsers[duplicatedInstagramUserIndex],
        duplicatedUsers,
      };

      return;
    }

    parsedUsers.push(user);
  });

  return parsedUsers;
};

class WarRoomService extends Service {
  constructor(gateway) {
    const api = createApi('/war-room', gateway, {
      authNotFound: () => true,
    });

    super(api, {
      afterParser,
      retriesLimit: 3,
    });

    this.api.http.defaults.timeout = 300000;
  }

  monitoringOverview(params = {}) {
    return this.find({
      ...params,
      url: '/monitoring-overview',
    });
  }

  topInfluencers(params = {}) {
    return this.find({
      ...params,
      url: '/top-influencers',
    });
  }

  topPosts(params = {}) {
    return this.find({
      ...params,
      url: '/top-posts',
    });
  }

  pressPosts(params = {}) {
    return this.find({
      ...params,
      url: '/press-posts',
    });
  }

  termGraph(params = {}) {
    return this.find({
      ...params,
      url: '/term-graph',
    });
  }

  postInTime(params = {}) {
    return this.find({
      ...params,
      url: '/post-in-time',
    });
  }

  monitoringMultibar(params = {}) {
    return this.find({
      ...params,
      url: '/monitoring-multibar',
    });
  }

  async searchUser(params = {}) {
    const { response } = await this.find({
      ...params,
      url: '/search-user',
    });

    return groupDuplicatedUsers(removeGMBUsers(response.docs || []));
  }

  async encodeFilter(params = {}) {
    if (!params.filter) {
      throw new Error('Invalid filter');
    }

    const filterHash = CryptoJS.MD5(JSON.stringify(params.filter));
    const cacheKey = `${params.is_staging ? 's' : 'p'}${filterHash}`;
    const filterId = localStorage.getItem(cacheKey);

    if (filterId) {
      return filterId;
    }

    const { data } = await this.update({
      ...params,
      url: `/encode-filter?is_staging=${Boolean(params.is_staging)}`,
    });

    try {
      localStorage.setItem(cacheKey, data);
    } catch (error) {
      Sentry.captureMessage('Error to store encoded filter', {
        ...params,
        filterHash,
        cacheKey,
      });
    }

    return data;
  }

  async getMonitoringOverviewWithVariation({ params = {} }) {
    const { originalParams, displacedParams } = displaceDateRange(params);

    const [data, displacedData] = await Promise.all([
      this.monitoringOverview({ params: originalParams }),
      this.monitoringOverview({ params: displacedParams }),
    ]);

    return {
      ...data,
      previous_channels: displacedData.channels,
      previous_total_users: displacedData.total_users,
      previous_total_localized_mentions: displacedData.total_localized_mentions,
      previous_total_users_variation: displacedData.total_users_variation,
      previous_displaced_collected_mentions: displacedData.previous_collected_mentions,
    };
  }
}

export default new WarRoomService(gateway);
